import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LockKey, ShieldCheck, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    }
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const result = await login(email, password);
    setBusy(false);
    if (result.ok) {
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } else {
      setErr(result.error);
    }
  };

  return (
    <div
      className="login-hero relative flex min-h-screen flex-col items-center justify-center px-6 py-12"
      data-testid="login-page"
    >
      {/* top status pill */}
      <div className="absolute top-6 right-6 z-10">
        <span className="ring-ribbon inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-mono-alt uppercase tracking-[0.25em]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
          System Operational
        </span>
      </div>

      {/* Logo */}
      <div className="relative z-10 mb-10 flex justify-center">
        <img
          src="/descon-logo-white.png"
          alt="Descon Group"
          style={{ height: 96, width: "auto", objectFit: "contain" }}
        />
      </div>

      {/* Centered card */}
      <div
        className="relative z-10 w-full max-w-md rounded-2xl px-8 py-10 md:px-10"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow: "0 20px 60px -20px rgba(0,0,0,0.5)",
        }}
        data-testid="login-card"
      >
        <div className="mb-5 flex items-center justify-center gap-2">
          <ShieldCheck size={14} weight="fill" style={{ color: "#8FB4F5" }} />
          <span className="overline" style={{ color: "#8FB4F5" }}>Secure access</span>
        </div>

        <h2 className="text-center font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
          Welcome back.
        </h2>
        <p className="mt-2 text-center text-sm" style={{ color: "#B9C4DC" }}>
          Sign in with your <span className="font-semibold text-white">descon.group</span> corporate account to continue.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <Label className="overline" style={{ color: "#8FB4F5" }}>Corporate email</Label>
            <Input
              type="email"
              placeholder="name@descon.group"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input mt-2 h-12"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label className="overline" style={{ color: "#8FB4F5" }}>Password</Label>
            <div className="relative mt-2">
              <Input
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input h-12 pr-11"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                data-testid="toggle-password-visibility"
                aria-label="Toggle password visibility"
              >
                {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {err && (
            <div
              className="rounded-lg border px-3 py-2.5 text-sm"
              style={{
                background: "rgba(248,113,113,0.12)",
                borderColor: "rgba(248,113,113,0.35)",
                color: "#fecaca",
              }}
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="btn-primary-blue group h-12 w-full text-sm font-semibold uppercase tracking-[0.18em]"
            data-testid="login-submit-button"
          >
            {busy ? "Authenticating…" : (
              <span className="inline-flex items-center gap-2">
                Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            )}
          </Button>

          <div
            className="flex items-start gap-2 rounded-lg p-3 text-[11px]"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#B9C4DC",
            }}
          >
            <LockKey size={14} className="mt-0.5 shrink-0" style={{ color: "#8FB4F5" }} />
            <span>
              Credentials are issued by your administrator. All activity is logged with IP address for audit purposes.
              Contact IT if you are locked out.
            </span>
          </div>
        </form>
      </div>

      {/* footer */}
      <div
        className="relative z-10 mt-10 text-center font-mono-alt text-[10px] uppercase tracking-[0.3em]"
        style={{ color: "#6E7FA5" }}
      >
        © Descon Group · Internal use only · Unauthorized access prohibited
      </div>
    </div>
  );
}
